import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import api from './services/api';
import Home from './pages/Home.jsx';
import Login from './pages/Login.jsx';
import Quiz from './pages/Quiz.jsx';
import Cards from './pages/Cards.jsx';
import Curiosaurus from './pages/Curiosaurus.jsx';
import Connections from './pages/Connections.jsx';
import Ranking from './pages/Ranking.jsx';
import Blitz from './pages/Blitz.jsx';
import Achievements from './pages/Achievements.jsx';
import Unlock from './pages/Unlock.jsx';
import Duels from './pages/Duels.jsx';
import Friends from './pages/Friends.jsx';
import Profile from './pages/Profile.jsx';
import Summary from './pages/Summary.jsx';
import Admin from './pages/Admin.jsx';
import Credits from './pages/Credits.jsx';
import DuelToast from './components/DuelToast.jsx';
import DuelResultPopup from './components/DuelResultPopup.jsx';
import ForestBackground from './components/ForestBackground.jsx';
import { useDuelNotifications } from './hooks/useDuelNotifications.js';
import './App.css';

// Auth context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('curiosaurus_token');
    if (token) {
      api.setToken(token);
      // Token GLISSANT : à chaque ouverture, le serveur redonne un token neuf → on
      // reste connecté tant qu'on ouvre l'app dans la fenêtre, et le token est frais
      // avant toute partie (donc jamais d'expiration en pleine partie).
      api.refreshToken()
        .then(res => { api.setToken(res.access_token); setUser(res.user); })
        .catch(() => { api.setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (credentials) => {
    const res = await api.login(credentials);
    api.setToken(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const register = async (data) => {
    const res = await api.register(data);
    api.setToken(res.access_token);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    api.setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-rocket">🚀</div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function GlobalDuelNotifier() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { event, dismissEvent, duelResults, dismissDuelResult, refresh } = useDuelNotifications(user?.id);
  // Pas de notification pendant qu'on joue (quiz solo, blitz, ou duel en cours)
  // — éviter de distraire ou qu'on clique dessus par erreur en plein jeu.
  const path = location.pathname;
  const isPlaying =
    path.startsWith('/quiz/') ||
    path === '/blitz' ||
    (path === '/duels' && window.__curioDuelActive === true);

  // Quand on sort du jeu, re-check tout de suite (la popup d'un duel terminé
  // entre-temps surgit sans attendre le prochain poll de 30 s).
  useEffect(() => {
    if (!isPlaying) refresh();
  }, [isPlaying, refresh]);

  const handleRematch = async (r) => {
    if (r?.opponentId && r?.themeId) {
      try { await api.createDuel({ opponent_id: r.opponentId, theme_id: r.themeId }); } catch {}
    }
    dismissDuelResult();
    navigate('/duels');
  };

  if (isPlaying) return null;
  // Priorité : popup-score des duels terminés (un par un), puis toast défi/à-jouer.
  if (duelResults[0]) {
    return (
      <DuelResultPopup
        result={duelResults[0]}
        remaining={duelResults.length}
        onClose={dismissDuelResult}
        onRematch={handleRematch}
      />
    );
  }
  if (event) return <DuelToast event={event} onClose={dismissEvent} />;
  return null;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app">
          <ForestBackground />
          <GlobalDuelNotifier />
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/quiz/:sessionId" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
              <Route path="/summary/:sessionId" element={<ProtectedRoute><Summary /></ProtectedRoute>} />
              <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
              <Route path="/curiosaurus" element={<ProtectedRoute><Curiosaurus /></ProtectedRoute>} />
              <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
              <Route path="/ranking" element={<ProtectedRoute><Ranking /></ProtectedRoute>} />
              <Route path="/blitz" element={<ProtectedRoute><Blitz /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/duels" element={<ProtectedRoute><Duels /></ProtectedRoute>} />
              <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
              <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
              <Route path="/unlock" element={<ProtectedRoute><Unlock /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
              <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
            </Routes>
          </AnimatePresence>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
